import { Container, Title } from "./styles";
import React, { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";

import ErrorDescription from "../../../../ErrorDescription";
import { IconBaseProps } from "react-icons";
import { useField } from "@unform/core";

interface InputMaskProps extends InputHTMLAttributes<HTMLInputElement> {
    name: string;
    icon?: React.ComponentType<IconBaseProps>;
    description?: string;
    max_length?: number;
    is_just_view?: string;
}

const InputCurrency: React.FC<InputMaskProps> = ({ name, description, icon: Icon, disabled, max_length, is_just_view, ...rest }) => {

    const isJustViewActive = is_just_view !== undefined && is_just_view !== null ? true : false;
    const isJustView = is_just_view !== undefined && is_just_view !== null && is_just_view === "S" ? true : false;

    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const { fieldName, defaultValue, error, registerField } = useField(name);

    const maskCurrency = (value: string) => {
        if (!value) return '';
        
        const isNegative = value.trim().startsWith('-');
        const numericValue = value.replace(/\D/g, "");
        
        if (numericValue === '') return '';
        
        const formattedValue = (Number(numericValue) / 100).toLocaleString('pt-BR', { 
            style: 'currency', 
            currency: 'BRL' 
        });
        
        return isNegative ? `-${formattedValue}` : formattedValue;
    };

    const maskCurrencyDefault = (value: string) => {
        if (value === '') return '';
        const numValue = Number(value);
        return numValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const [currency, setCurrency] = React.useState(defaultValue !== undefined ? maskCurrencyDefault(defaultValue) : undefined);

    const handleInputFocused = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        setIsFocused(false);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        
        // Se o usuário digitar apenas o sinal de menos, preserve-o
        if (value === '-') {
            setCurrency('-');
            return;
        }
        
        setCurrency(maskCurrency(value));
    };

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: inputRef.current,
            getValue: () => {
                if (currency !== undefined) {
                    if (currency === '-') return 0;
                    
                    let value: string = currency;
                    const isNegative = value.trim().startsWith('-');
                    
                    // Remove o sinal negativo para processamento
                    if (isNegative) {
                        value = value.substring(1);
                    }
                    
                    value = value.replace(".", "").replace(".", "").replace(".", "").replace(",", ".");
                    
                    // Aplica o sinal negativo se necessário
                    const numValue = Number(value.replace(/[^0-9.]+/g, ""));
                    return value === "" ? undefined : (isNegative ? -numValue : numValue);
                } else {
                    return undefined;
                }
            },
            setValue: (ref, value) => {
                if (value !== null && value !== undefined) {
                    setCurrency(maskCurrencyDefault(String(value)));
                }
            },
            clearValue: () => {
                setCurrency(maskCurrencyDefault(""));
            }
        });

    }, [fieldName, registerField, currency]);

    return (
        <>
            {description && <Title>{description}</Title>}
            <Container isErrored={!!error} isFocused={isFocused} isDisabled={isJustViewActive ? isJustView : !!disabled} >
                {Icon && <Icon size="20" />}
                <input
                    id={name}
                    onFocus={handleInputFocused}
                    onBlur={handleInputBlur}
                    onChange={handleInputChange}
                    ref={inputRef}
                    disabled={isJustViewActive ? isJustView : !!disabled}
                    maxLength={max_length}
                    value={currency}
                    {...rest}
                />

            </Container>

            {error && (<ErrorDescription title={error} />)}
        </>
    );
}

export default InputCurrency;